import ConfirmDialog from '@/components/ConfirmDialog';
import ProfileDropdown from '@/components/ProfileDropdown';
import ProjectModal from '@/components/ProjectModal';
import { useToast } from '@/hooks/useToast';
import { projectsService } from '@/services/projectsService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Projects page component
 * @returns {JSX.Element} Projects component
 */
const Projects = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Fetch projects using React Query
  const {
    data: projectsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsService.getProjects,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  const projects = projectsData?.docs || [];

  // Debug: Log projects data

  // Mutations
  const createProjectMutation = useMutation({
    mutationFn: projectsService.createProject,
    onSuccess: () => {
      queryClient.invalidateQueries(['projects']);
      showSuccess('Project created successfully!');
      setIsCreateModalOpen(false);
    },
    onError: error => {
      showError(error?.response?.data?.message || 'Failed to create project');
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: ({ projectId, projectData }) =>
      projectsService.updateProject(projectId, projectData),
    onSuccess: () => {
      queryClient.invalidateQueries(['projects']);
      showSuccess('Project updated successfully!');
      setIsEditModalOpen(false);
      setProjectToEdit(null);
    },
    onError: error => {
      showError(error?.response?.data?.message || 'Failed to update project');
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: projectsService.deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries(['projects']);
      showSuccess('Project deleted successfully!');
      setIsDeleteDialogOpen(false);
      setProjectToDelete(null);
    },
    onError: error => {
      showError(error?.response?.data?.message || 'Failed to delete project');
    },
  });

  // Handle project selection and navigation
  const handleProjectClick = project => {
    setSelectedProject(project);
    // Navigate to project dashboard - handle both _id and id fields
    const projectId = project._id || project.id;
    if (projectId) {
      if (project.isWhatsappConnected) {
        navigate(`/projects/${projectId}/dashboard`);
      } else {
        navigate(`/projects/${projectId}/whatsapp-configurations`);
      }
    } else {
      console.error('No project ID found:', project);
      showError('Unable to navigate to project. Please try again.');
    }
  };

  // Handle create project
  const handleCreateProject = projectData => {
    createProjectMutation.mutate(projectData);
  };

  // Handle edit project
  const handleEditProject = project => {
    setProjectToEdit(project);
    setIsEditModalOpen(true);
  };

  const handleUpdateProject = projectData => {
    const projectId = projectToEdit._id || projectToEdit.id;
    updateProjectMutation.mutate({
      projectId,
      projectData,
    });
  };

  // Handle delete project
  const handleDeleteProject = project => {
    setProjectToDelete(project);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteProject = () => {
    const projectId = projectToDelete._id || projectToDelete.id;
    deleteProjectMutation.mutate(projectId);
  };

  // Handle error display
  useEffect(() => {
    if (error) {
      showError('Failed to load projects. Please try again.');
    }
  }, [error, showError]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="h-16 flex justify-between items-center px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/projects" className="flex-shrink-0">
              <img
                src="/Integrable_Logo.svg"
                alt="Integrable Logo"
                className="h-16 w-auto"
              />
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <ProfileDropdown />
          </div>
        </div>
      </header>

      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">My Projects</h1>
            <p className="mt-2 text-gray-600">Select a project to manage</p>
          </div>

          {/* Error state */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              <div className="flex items-center">
                <svg
                  className="h-5 w-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                Failed to load projects.
                <button
                  onClick={() => refetch()}
                  className="ml-2 underline hover:no-underline"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Projects Grid */}
          {projects.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-24 w-24 text-gray-300">
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                No projects found
              </h3>
              <p className="mt-2 text-gray-500">
                You haven't created any projects yet. Create your first project
                to get started.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Create Project
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {projects.map(project => (
                <div
                  key={project._id || project.id}
                  onClick={() => handleProjectClick(project)}
                  className={`relative bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md hover:border-primary-300 cursor-pointer ${
                    selectedProject?._id === project._id ||
                    selectedProject?.id === project.id
                      ? 'border-primary-500 shadow-md'
                      : 'border-gray-200'
                  }`}
                >
                  {/* Project Status Badge */}
                  <div className="absolute top-3 left-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        project.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {project.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="absolute top-2 right-2 flex space-x-1 z-20">
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleEditProject(project);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full shadow-md hover:shadow-lg border border-gray-200"
                      title="Edit project"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteProject(project);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors bg-white rounded-full shadow-md hover:shadow-lg border border-gray-200"
                      title="Delete project"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Project Content */}
                  <div className="p-6">
                    {/* Project Logo/Icon */}
                    <div className="flex items-center justify-center h-16 w-16 mx-auto mb-4 bg-primary-100 rounded-lg">
                      <svg
                        className="h-8 w-8 text-primary-600"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    </div>

                    {/* Project Name */}
                    <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                      {project.name}
                    </h3>

                    {/* Project Description */}
                    {project.description && (
                      <p className="text-sm text-gray-600 text-center mb-4 line-clamp-2">
                        {project.description}
                      </p>
                    )}

                    {/* Project Info */}
                    <div className="flex justify-between text-sm text-gray-500">
                      <div className="flex items-center">
                        <svg
                          className="h-4 w-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        {project.userId ? 'Owned' : 'Shared'}
                      </div>
                      <div className="flex items-center">
                        <svg
                          className="h-4 w-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        v{project.__v || 0}
                      </div>
                    </div>

                    {/* Created Date */}
                    <div className="mt-3 text-xs text-gray-400 text-center">
                      Created {new Date(project.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-primary-50 opacity-0 hover:opacity-10 transition-opacity duration-200 rounded-lg"></div>
                </div>
              ))}
            </div>
          )}

          {/* Create New Project Button */}
          {projects.length > 0 && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg
                  className="-ml-1 mr-2 h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Create New Project
              </button>
            </div>
          )}

          {/* Create Project Modal */}
          <ProjectModal
            isOpen={isCreateModalOpen}
            onSubmit={handleCreateProject}
            onCancel={() => setIsCreateModalOpen(false)}
            isLoading={createProjectMutation.isPending}
          />

          {/* Edit Project Modal */}
          <ProjectModal
            isOpen={isEditModalOpen}
            project={projectToEdit}
            onSubmit={handleUpdateProject}
            onCancel={() => {
              setIsEditModalOpen(false);
              setProjectToEdit(null);
            }}
            isLoading={updateProjectMutation.isPending}
          />

          {/* Delete Confirmation Dialog */}
          <ConfirmDialog
            isOpen={isDeleteDialogOpen}
            title="Delete Project"
            message={`Are you sure you want to delete "${projectToDelete?.name}"? This action cannot be undone.`}
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={confirmDeleteProject}
            onCancel={() => {
              setIsDeleteDialogOpen(false);
              setProjectToDelete(null);
            }}
            isLoading={deleteProjectMutation.isPending}
            variant="danger"
          />
        </div>
      </div>
    </div>
  );
};

export default Projects;
